// Poin loyalti: perolehan per-transaksi + masa berlaku + penukaran.
//
// Kenapa per-transaksi (bukan sekadar total belanja ÷ rate): poin harus bisa HANGUS, dan
// sesuatu hanya bisa hangus kalau diketahui kapan ia lahir. Umurnya dihitung dari TANGGAL
// UNIT DIAMBIL — saat itulah pelanggan benar-benar selesai bertransaksi.

export interface BatchPoin {
  /** Nomor servis asalnya — supaya pelanggan bisa menelusuri "poin ini dari servis mana". */
  no: string;
  /** Tanggal unit diambil (yyyy-mm-dd). */
  tgl: string;
  poin: number;
  /** Tanggal poin ini hangus (yyyy-mm-dd); null = tak pernah hangus. */
  hangus: string | null;
}

export const tambahBulan = (tgl: string, bulan: number): string => {
  const d = new Date(`${tgl}T00:00:00Z`);
  if (isNaN(d.getTime())) return tgl;
  const hariAsli = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + bulan);
  // 31 Jan + 1 bulan = 3 Mar di JS. Mundurkan ke akhir bulan supaya tak melompati satu bulan.
  if (d.getUTCDate() < hariAsli) d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
};

/**
 * Ubah daftar transaksi jadi batch poin.
 *
 * `sisaRupiah` dibawa antar-transaksi: kalau tiap servis dibulatkan ke bawah sendiri-sendiri,
 * pelanggan kehilangan sisa belanjanya berkali-kali dan total poinnya turun dibanding cara
 * lama (total ÷ rate). Dengan membawa sisa, totalnya persis sama — hanya kini punya umur.
 */
export function batchPoin(
  trx: { no: string; tgl: string; nilai: number }[],
  rate: number,
  berlakuBulan: number,
): BatchPoin[] {
  const r = Math.max(1, rate);
  const urut = [...trx].sort((a, b) => a.tgl.localeCompare(b.tgl));
  const out: BatchPoin[] = [];
  let sisa = 0;
  for (const t of urut) {
    const total = sisa + Math.max(0, t.nilai);
    const poin = Math.floor(total / r);
    sisa = total - poin * r;
    if (poin > 0) out.push({ no: t.no, tgl: t.tgl, poin, hangus: berlakuBulan > 0 ? tambahBulan(t.tgl, berlakuBulan) : null });
  }
  return out;
}

/** Batch yang sudah lewat tanggal hangus pada `hariIni`. */
export const sudahHangus = (b: BatchPoin, hariIni: string): boolean => !!b.hangus && b.hangus < hariIni;

export interface SaldoPoin {
  tersedia: number;        // siap dipakai hari ini
  hangusNanti: number;     // bagian dari `tersedia` yang hangus paling dekat
  tglHangusTerdekat: string | null;
  totalDiperoleh: number;
  totalHangus: number;
  totalDitukar: number;
}

/**
 * Saldo poin. Penukaran memakan batch yang PALING CEPAT HANGUS lebih dulu (FIFO) — kalau
 * tidak, poin yang hampir kedaluwarsa akan menganggur lalu hangus padahal pelanggan sudah
 * menukar, dan ia merasa dicurangi.
 */
export function saldoPoin(batch: BatchPoin[], sudahDitukar: number, hariIni: string): SaldoPoin {
  const urut = [...batch].sort((a, b) => (a.hangus ?? '9999-12-31').localeCompare(b.hangus ?? '9999-12-31'));
  let sisaTukar = Math.max(0, sudahDitukar);
  let tersedia = 0, totalHangus = 0;
  const hidup: BatchPoin[] = [];
  for (const b of urut) {
    const pakai = Math.min(sisaTukar, b.poin);
    sisaTukar -= pakai;
    const sisaBatch = b.poin - pakai;
    if (!sisaBatch) continue;
    if (sudahHangus(b, hariIni)) totalHangus += sisaBatch;
    else { tersedia += sisaBatch; hidup.push({ ...b, poin: sisaBatch }); }
  }
  const terdekat = hidup.find((b) => b.hangus) ?? null;
  return {
    tersedia,
    hangusNanti: terdekat ? hidup.filter((b) => b.hangus === terdekat.hangus).reduce((a, b) => a + b.poin, 0) : 0,
    tglHangusTerdekat: terdekat?.hangus ?? null,
    totalDiperoleh: batch.reduce((a, b) => a + b.poin, 0),
    totalHangus,
    totalDitukar: Math.max(0, sudahDitukar),
  };
}

/** Kode tukar 6 angka. Dibuat DI PERANGKAT PELANGGAN, bukan oleh kasir. */
export const kodeTukarValid = (kode: string): boolean => /^\d{6}$/.test(kode.trim());

/** Kode kedaluwarsa (menit) — sengaja pendek: tangkapan layar lama tak boleh bisa dipakai. */
export const KODE_TUKAR_MENIT = 10;
export const kodeMasihHidup = (dibuatMs: number, kiniMs: number, menit = KODE_TUKAR_MENIT): boolean =>
  kiniMs - dibuatMs < menit * 60_000;

// ── Program afiliasi (referral) ────────────────────────────────────────────────
// Poin afiliasi DIPISAH dari poin servis (keputusan Apo): poin servis ikut hilang sendiri
// saat servis di-refund karena dihitung dari omzet, sedangkan bonus afiliasi adalah pemberian
// tetap yang harus DITARIK secara sengaja — dan penarikannya wajib punya alasan tercatat.

export interface AfiliasiCfg {
  aktif: boolean;
  /** Poin untuk perekomendasi tiap rekomendasi yang jadi. */
  bonus: number;
  /** Maksimal rekomendasi berbonus per bulan per orang. 0 = tanpa batas. */
  batasBulan: number;
  /** Masa berlaku poin afiliasi (bulan). 0 = tak hangus. */
  berlakuBulan: number;
  /** Poin untuk pelanggan baru yang direkomendasikan. 0 = mati. */
  bonusPendatang: number;
}
export const DEFAULT_AFILIASI: AfiliasiCfg = { aktif: false, bonus: 50, batasBulan: 5, berlakuBulan: 12, bonusPendatang: 0 };

export type AlasanTolak = 'mati' | 'diri-sendiri' | 'batas-bulan' | 'sudah-diberi' | 'belum-lunas';

export interface CekBonus { boleh: boolean; alasan?: AlasanTolak; poin: number }

/**
 * Boleh memberi bonus afiliasi?
 *
 * Bonus baru cair setelah servis pertama orang yang direkomendasikan LUNAS & diverifikasi:
 * poin adalah kewajiban toko yang berujung barang nyata, jadi ia hanya boleh lahir setelah
 * uangnya benar-benar masuk. Kalau cair saat pendaftaran, poin bisa diciptakan hanya dengan
 * mengetik nama.
 */
export function cekBonusAfiliasi(p: {
  cfg: AfiliasiCfg;
  perekomendasiId: number;
  pelangganBaruId: number;
  /** WA/telepon keduanya — dibandingkan untuk menangkal orang mereferensikan dirinya sendiri. */
  waPerekomendasi?: string | null;
  waPelangganBaru?: string | null;
  lunasTerverifikasi: boolean;
  /** Berapa bonus yang sudah diterima perekomendasi pada bulan yang sama. */
  bonusBulanIni: number;
  /** true bila tiket ini sudah pernah memicu bonus (cegah bayar dua kali). */
  sudahDiberi: boolean;
}): CekBonus {
  const { cfg } = p;
  if (!cfg.aktif || cfg.bonus <= 0) return { boleh: false, alasan: 'mati', poin: 0 };
  if (p.sudahDiberi) return { boleh: false, alasan: 'sudah-diberi', poin: 0 };
  if (!p.lunasTerverifikasi) return { boleh: false, alasan: 'belum-lunas', poin: 0 };
  // Nomor sama = orang yang sama memakai nomor kedua; id sama = jelas dirinya sendiri.
  const wa1 = (p.waPerekomendasi ?? '').replace(/\D/g, '');
  const wa2 = (p.waPelangganBaru ?? '').replace(/\D/g, '');
  if (p.perekomendasiId === p.pelangganBaruId || (wa1 && wa1 === wa2)) return { boleh: false, alasan: 'diri-sendiri', poin: 0 };
  if (cfg.batasBulan > 0 && p.bonusBulanIni >= cfg.batasBulan) return { boleh: false, alasan: 'batas-bulan', poin: 0 };
  return { boleh: true, poin: cfg.bonus };
}

export const ALASAN_TOLAK: Record<AlasanTolak, string> = {
  'mati': 'Program afiliasi sedang tidak aktif.',
  'diri-sendiri': 'Perekomendasi dan pelanggan baru orang yang sama.',
  'batas-bulan': 'Perekomendasi sudah mencapai batas bonus bulan ini.',
  'sudah-diberi': 'Servis ini sudah pernah memberi bonus.',
  'belum-lunas': 'Menunggu servis pertama lunas & diverifikasi Finance.',
};

/** Poin pemberian (afiliasi/koreksi) yang belum ditarik → ikut jadi batch bersama poin servis. */
export function batchPemberian(
  grants: { id: number; tgl: string; poin: number; alasan: string; ditarik: boolean; berlakuBulan: number }[],
): BatchPoin[] {
  return grants.filter((g) => !g.ditarik && g.poin > 0).map((g) => ({
    no: g.alasan, tgl: g.tgl, poin: g.poin,
    hangus: g.berlakuBulan > 0 ? tambahBulan(g.tgl, g.berlakuBulan) : null,
  }));
}

/**
 * Bagi sebuah penukaran ke dua sumber poin: servis dan afiliasi.
 *
 * Saldonya SATU (pelanggan melihat satu angka dan menukarnya utuh), tapi angkanya tetap
 * dipisah supaya toko tahu berapa hadiah yang "dibayar" oleh belanja pelanggan dan berapa
 * yang dibayar oleh program rekomendasi — dua hal itu punya biaya dan makna berbeda.
 *
 * `porsiServisPersen` = target porsi dari poin servis (mis. 70 → 70:30). Bila salah satu
 * sumber tak cukup, kekurangannya diambil dari sumber lain — pelanggan tak boleh gagal
 * menukar hanya karena komposisi poinnya tidak pas.
 */
export function bagiPemakaian(
  butuh: number,
  tersediaServis: number,
  tersediaAfiliasi: number,
  porsiServisPersen: number,
): { servis: number; afiliasi: number; cukup: boolean } {
  const total = Math.max(0, tersediaServis) + Math.max(0, tersediaAfiliasi);
  if (butuh <= 0 || total < butuh) return { servis: 0, afiliasi: 0, cukup: false };
  const p = Math.min(100, Math.max(0, porsiServisPersen));
  let servis = Math.min(Math.max(0, tersediaServis), Math.round((butuh * p) / 100));
  let afiliasi = Math.min(Math.max(0, tersediaAfiliasi), butuh - servis);
  // Sisa karena salah satu sumber kurang → tambal dari sumber lain.
  const kurang = butuh - servis - afiliasi;
  if (kurang > 0) {
    const tambahS = Math.min(kurang, Math.max(0, tersediaServis) - servis);
    servis += tambahS;
    afiliasi += Math.min(kurang - tambahS, Math.max(0, tersediaAfiliasi) - afiliasi);
  }
  return { servis, afiliasi, cukup: servis + afiliasi === butuh };
}
